import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { HealthCheck } from "@/components/site/HealthCheck";
import { BRAND } from "@/data/content";

// Superhero posters, in order. The final poster carries the website + phone CTA.
const POSTERS = [
  { src: "/assets/rescue-hero.mp4", alt: "Captain Save A Website — animated", video: true },
  { src: "/assets/hero-2-captain.png", alt: "Have a website that needs saving?" },
  { src: "/assets/hero-4-cooldaddy.png", alt: "Cool Daddy, live from the cosmos." },
  { src: "/assets/hero-5-convergence.png", alt: "A convergence of heroes vs. The Brand." },
  { src: "/assets/hero-3-csw.png", alt: "Captain Save A Website — EmergentSac.com · 916-WEB-8101" },
];

export default function SaveAWebsite() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Save A Website — Website Rescue & Redesign Sacramento | Emergent Sacramento";
  }, []);

  return (
    <div className="bg-ink text-white font-body min-h-screen">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-36 pb-16 border-b border-line text-center">
          <div className="container-x">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mono-label text-accent"
            >
              [ To the rescue ]
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-head font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-5 text-balance leading-[0.95]"
            >
              Save A Website.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-lg text-text-2 leading-relaxed max-w-xl mx-auto"
            >
              Site down? Data a mess? Old webmaster vanished? Captain Save A Website is on call for
              Sacramento businesses.
            </motion.p>
          </div>
        </section>

        {/* Full-width posters in order */}
        <section className="relative">
          {POSTERS.map((p, i) => (
            <motion.div
              key={p.src}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="w-full bg-ink border-b border-line"
            >
              {p.video ? (
                <video
                  data-testid="rescue-hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  poster="/assets/hero-3-csw.png"
                  className="w-full h-auto block"
                >
                  <source src="/assets/rescue-hero.webm" type="video/webm" />
                  <source src="/assets/rescue-hero.mp4" type="video/mp4" />
                </video>
              ) : (
                <img src={p.src} alt={p.alt} className="w-full h-auto block" loading={i === 0 ? "eager" : "lazy"} />
              )}
            </motion.div>
          ))}
        </section>

        {/* Free Site Audit */}
        <HealthCheck />

        {/* CTA */}
        <section className="relative py-20 text-center">
          <div className="container-x">
            <h2 className="font-head font-black tracking-tighter text-3xl sm:text-5xl text-balance max-w-3xl mx-auto">
              Ready to be rescued?
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#contact"
                data-testid="save-cta-quote"
                className="inline-flex items-center gap-2 bg-accent text-ink font-head font-bold px-8 py-4 hover:bg-white transition-colors group"
              >
                Get a free rescue plan
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <a
                href={`tel:${BRAND.phoneTel}`}
                data-testid="save-cta-call"
                className="inline-flex items-center gap-2 border border-line text-white font-head font-semibold px-8 py-4 hover:border-accent hover:text-accent transition-colors"
              >
                <Phone className="w-5 h-5" /> {BRAND.phone}
              </a>
            </div>
            <p className="mono-label text-text-2 mt-6">{BRAND.email} · EmergentSac.com</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
